import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Crown, Check, Shield, Calendar, Mic, Plane, Users } from 'lucide-react';
import StripePaymentButton from '../components/Profile/StripePaymentButton';
import { ApplePayButton } from '../components/Profile/ApplePayButton';
import { useSubscription } from '../contexts/SubscriptionContext';

export function Subscription() {
  const [error, setError] = useState<string | null>(null);
  const { subscription } = useSubscription();
  const [loading, setLoading] = useState(false);

  const features = [
    { 
      name: 'Daily Task View', 
      description: 'Track and manage your tasks by day',
      icon: Calendar
    },
    { 
      name: 'Custom Roles', 
      description: 'Create and manage custom roles',
      icon: Shield
    },
    { 
      name: 'Collaboration', 
      description: 'Work together with team members',
      icon: Users
    },
    { 
      name: 'Voice Notes', 
      description: 'Record voice notes',
      icon: Mic
    },
    { 
      name: 'Travel Organizer', 
      description: 'Plan and organize your travels',
      icon: Plane
    }
  ];

  const handlePaymentSuccess = () => {
    setError(null);
    window.location.href = '/profile';
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
    setLoading(false);
  };

  const isSubscribed = subscription?.status === 'active' && 
    subscription.current_period_end && 
    new Date(subscription.current_period_end) > new Date();

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center space-y-4 mb-12">
          <div className="w-16 h-16 rounded-full bg-[var(--accent-color)] flex items-center justify-center mx-auto">
            <Crown className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-4xl font-bold">Upgrade to Premium</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Get access to all premium features and take your productivity to the next level
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Features List */}
          <div className="bg-[#2A2B2E] rounded-3xl p-8 space-y-6">
            <h2 className="text-2xl font-bold mb-6">Everything you need</h2>
            <div className="space-y-6">
              {features.map((feature) => (
                <div key={feature.name} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[var(--accent-color)]/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-[var(--accent-color)]" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">{feature.name}</h3>
                    <p className="text-sm text-gray-400">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Card */}
          <div className="bg-[#2A2B2E] rounded-3xl p-8">
            <div className="text-center mb-8">
              <div className="text-4xl font-bold mb-2">
                $9.99 <span className="text-lg text-gray-400">/month</span>
              </div>
              <p className="text-gray-400">Cancel anytime</p>
            </div>

            <div className="space-y-4 mb-8">
              {features.map((feature) => (
                <div key={feature.name} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[var(--accent-color)]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[var(--accent-color)]" />
                  </div>
                  <span>{feature.name}</span>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {!isSubscribed && (
                <>
                  <StripePaymentButton
                    amount={9.99}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    disabled={loading}
                  />
                  <ApplePayButton
                    amount={9.99}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    disabled={loading}
                  />
                </>
              )}
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-500/10 text-red-500 rounded-xl text-sm text-center">
                {error}
              </div>
            )}

            {isSubscribed && (
              <div className="mt-4 p-4 bg-green-500/10 text-green-500 rounded-xl text-sm text-center">
                You already have an active subscription! Go to your <a href="/profile" className="underline">profile</a> to manage it.
              </div>
            )}

            <p className="text-xs text-gray-400 text-center mt-6">
              By subscribing, you agree to our Terms of Service and Privacy Policy.
              Your subscription will automatically renew each month until canceled.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}